import {
  MapLakehouseRoleParams,
  UnifiedLakehousePolicy,
} from "@/api/temp/UnifiedLakehouseService";
import lakehouseSeed from "./seed";

/**
 * Mock store backing the two Unified Lakehouse mutations that still have no
 * real backend endpoint: Map Native Role and drift-mode Deploy. Catalog and
 * policy listing are GA and hit the real API - this store only exists so
 * those two demo-only mutations can look up and mutate a policy by id.
 * Session-scoped: state lives in module memory and is cleared on logout via
 * resetAllMockStores().
 */
class LakehouseStore {
  private policies: UnifiedLakehousePolicy[] = LakehouseStore.clone(
    lakehouseSeed.policies,
  );

  private static clone<T>(value: T): T {
    return structuredClone(value);
  }

  private getPolicy(id: string): UnifiedLakehousePolicy | undefined {
    return this.policies.find(p => p.id === id);
  }

  /**
   * Map (or auto-create) the native role for one datasource of a policy.
   * Mirrors the mockup `getEffectiveRoleMappings`/`getEffectivePolicy`: the
   * resolved row flips to Mapped/Auto Created and its platform action becomes
   * "Deploy Policy".
   */
  mapRole(params: MapLakehouseRoleParams): UnifiedLakehousePolicy | undefined {
    const { policyId, dataSourceId, nativeRole, autoCreate } = params;
    const resolvedRole =
      nativeRole?.trim() || (autoCreate ? "FINANCE_ANALYST" : "finance_analyst");

    this.policies = this.policies.map(policy => {
      if (policy.id !== policyId) return policy;
      return {
        ...policy,
        roleMappings: (policy.roleMappings ?? []).map(mapping =>
          mapping.dataSourceId === dataSourceId
            ? {
                ...mapping,
                nativeRole: resolvedRole,
                status: autoCreate ? "Auto Created" : "Mapped",
              }
            : mapping,
        ),
        platformCoverage: policy.platformCoverage.map(item =>
          item.dataSourceId === dataSourceId &&
          item.roleMappingStatus === "Needs Mapping"
            ? {
                ...item,
                roleMappingStatus: "Mapped",
                action:
                  item.policyStatus === "Missing" ? "Deploy Policy" : "No Action",
              }
            : item,
        ),
      };
    });

    return this.getPolicy(policyId);
  }

  /**
   * Deploy a policy everywhere: coverage becomes Fully Covered and every
   * platform row flips to Deployed / No Action.
   */
  deploy(policyId: string): UnifiedLakehousePolicy | undefined {
    let deployed: UnifiedLakehousePolicy | undefined;

    this.policies = this.policies.map(policy => {
      if (policy.id !== policyId) return policy;
      deployed = {
        ...policy,
        coverageStatus: "Fully Covered",
        roleMappings: (policy.roleMappings ?? []).map(mapping =>
          mapping.status === "Needs Mapping"
            ? { ...mapping, status: "Mapped", nativeRole: mapping.nativeRole ?? "finance_analyst" }
            : mapping,
        ),
        platformCoverage: policy.platformCoverage.map(item => ({
          ...item,
          policyStatus: "Deployed",
          roleMappingStatus:
            item.roleMappingStatus === "Needs Mapping"
              ? "Mapped"
              : item.roleMappingStatus,
          action: "No Action",
        })),
      };
      return deployed;
    });

    return deployed;
  }

  reset(): void {
    this.policies = LakehouseStore.clone(lakehouseSeed.policies);
  }
}

export const lakehouseStore = new LakehouseStore();